import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ImportProfileCreateSchema,
  SOURCE_FORMATS,
  type ImportProfileCreate,
  type ImportProfileResponse,
} from '@flixflow/shared';
import { importProfilesApi } from '../api/importProfiles.js';
import type { ConnectionProfileResponse } from '@flixflow/shared';
import { apiFetch } from '../api/client.js';

function useConnections() {
  return useQuery({
    queryKey: ['connections'],
    queryFn: () => apiFetch<ConnectionProfileResponse[]>('/api/connection-profiles'),
  });
}

function ProfileModal({
  profile,
  onClose,
}: {
  profile?: ImportProfileResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: connections = [] } = useConnections();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ImportProfileCreate>({
    resolver: zodResolver(ImportProfileCreateSchema),
    defaultValues: profile
      ? {
          name: profile.name,
          bereich: profile.bereich,
          targetTable: profile.targetTable,
          connectionId: profile.connectionId,
          sourceFormat: profile.sourceFormat,
          sheetName: profile.sheetName ?? undefined,
          hasHeader: profile.hasHeader,
          onlyChanged: profile.onlyChanged,
          isActive: profile.isActive,
        }
      : {
          sourceFormat: 'EXCEL',
          hasHeader: true,
          onlyChanged: true,
          isActive: true,
        },
  });

  const sourceFormat = watch('sourceFormat');

  const mut = useMutation({
    mutationFn: (data: ImportProfileCreate) =>
      profile
        ? importProfilesApi.update(profile.id, data)
        : importProfilesApi.create(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['import-profiles'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <h2 className="text-lg font-semibold mb-4">
          {profile ? 'Profil bearbeiten' : 'Neues Importprofil'}
        </h2>

        <form
          onSubmit={handleSubmit((d) => mut.mutate(d))}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              {...register('name')}
              className="w-full border rounded px-3 py-1.5 text-sm"
              placeholder="z. B. Artikel-Import täglich"
            />
            {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Bereich</label>
              <input
                {...register('bereich')}
                className="w-full border rounded px-3 py-1.5 text-sm"
                placeholder="Artikel"
              />
              {errors.bereich && <p className="text-red-600 text-xs mt-1">{errors.bereich.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zieltabelle (MSSQL)</label>
              <input
                {...register('targetTable')}
                className="w-full border rounded px-3 py-1.5 text-sm"
                placeholder="Artikel"
              />
              {errors.targetTable && (
                <p className="text-red-600 text-xs mt-1">{errors.targetTable.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Verbindung</label>
            <select {...register('connectionId')} className="w-full border rounded px-3 py-1.5 text-sm">
              <option value="">-- Verbindung wählen --</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.host}/{c.database})
                </option>
              ))}
            </select>
            {errors.connectionId && (
              <p className="text-red-600 text-xs mt-1">{errors.connectionId.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Format</label>
              <select {...register('sourceFormat')} className="w-full border rounded px-3 py-1.5 text-sm">
                {SOURCE_FORMATS.map((f) => (
                  <option key={f} value={f}>
                    {f === 'EXCEL' ? 'Excel (.xlsx/.xls)' : 'CSV (Semikolon)'}
                  </option>
                ))}
              </select>
            </div>

            {sourceFormat === 'EXCEL' && (
              <div>
                <label className="block text-sm font-medium mb-1">Sheet-Name (optional)</label>
                <input
                  {...register('sheetName')}
                  className="w-full border rounded px-3 py-1.5 text-sm"
                  placeholder="Tabelle1"
                />
              </div>
            )}
          </div>

          <div className="flex gap-6 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('hasHeader')} />
              Erste Zeile = Header
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('onlyChanged')} />
              Nur geänderte Datensätze
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('isActive')} />
              Aktiv
            </label>
          </div>

          {mut.error && (
            <p className="text-red-600 text-sm">{(mut.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border rounded hover:bg-neutral-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isSubmitting || mut.isPending}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
            >
              {profile ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ImportProfilesPage() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editProfile, setEditProfile] = useState<ImportProfileResponse | undefined>();

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['import-profiles'],
    queryFn: () => importProfilesApi.list(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => importProfilesApi.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['import-profiles'] }),
  });

  const openCreate = () => {
    setEditProfile(undefined);
    setShowModal(true);
  };

  const openEdit = (p: ImportProfileResponse) => {
    setEditProfile(p);
    setShowModal(true);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Importprofile</h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          + Neues Profil
        </button>
      </div>

      {isLoading ? (
        <p className="text-neutral-500 text-sm">Lade Profile…</p>
      ) : profiles.length === 0 ? (
        <div className="text-center py-16 text-neutral-400">
          <p className="text-base mb-1">Noch keine Importprofile angelegt.</p>
          <p className="text-sm">Erstelle ein Profil, um Daten in deine MSSQL-Datenbank zu importieren.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Bereich</th>
                <th className="px-4 py-3 text-left font-medium">Zieltabelle</th>
                <th className="px-4 py-3 text-left font-medium">Format</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {profiles.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/import-profile/${p.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{p.bereich}</td>
                  <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{p.targetTable}</td>
                  <td className="px-4 py-3 text-neutral-600">
                    {p.sourceFormat === 'EXCEL' ? 'Excel' : 'CSV'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        p.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {p.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <Link
                      to={`/import-profile/${p.id}`}
                      className="text-blue-600 text-xs hover:underline"
                    >
                      Mappings
                    </Link>
                    <button
                      onClick={() => openEdit(p)}
                      className="text-neutral-600 text-xs hover:text-neutral-900"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Profil "${p.name}" wirklich löschen?`)) {
                          deleteMut.mutate(p.id);
                        }
                      }}
                      className="text-red-500 text-xs hover:text-red-700"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProfileModal
          profile={editProfile}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
